import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ShieldCheck, Radar, Activity, AlertTriangle, ArrowRight, Linkedin, MessageSquare, FileText, Download, BookOpen, Compass, Handshake } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import LeadCaptureModal from '../components/LeadCaptureModal';
import SEO from '../components/SEO';

const LandingPage = () => {
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [modalType, setModalType] = useState('diagnostic');

  const domains = [
    {
      icon: ShieldCheck,
      title: 'Evidence Integrity',
      description: 'Are system decisions explainable? Are failures visible or hidden from users and operators?'
    },
    {
      icon: Radar,
      title: 'Identity Credibility',
      description: 'Can users bypass or manipulate flows? Are validations consistently enforced across all entry points?'
    },
    {
      icon: Activity,
      title: 'Operational Visibility',
      description: 'Are errors logged and surfaced? Can you detect delays, inconsistencies, and anomalies in real-time?'
    },
    {
      icon: AlertTriangle,
      title: 'Control Architecture',
      description: 'Are safeguards properly layered? Are edge cases handled and critical flows protected from failures?'
    }
  ];

  const serviceFlow = [
    {
      number: '01',
      title: 'Discovery Scan',
      description: 'Surface-level automated diagnostic to identify visible trust signals and friction points.'
    },
    {
      number: '02',
      title: 'Structured Review',
      description: 'Deep investigation of critical flows with expert analysis and commercial impact assessment.'
    },
    {
      number: '03',
      title: 'Ongoing Monitoring',
      description: 'Continuous trust and revenue integrity oversight as your product evolves.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#0B0B0D] flex flex-col">
      <SEO
        title="Subsurface | Trust & Risk Architecture. Find Where Your Product Loses User Trust"
        description="Subsurface is a Trust and Risk Architecture consultancy helping founders and product teams identify where digital products quietly lose user trust before it costs revenue."
        path="/"
      />
      <Navigation />

      {/* Hero Section */}
      <motion.section 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="relative overflow-hidden pt-32 pb-24"
      >
        <div className="absolute inset-0 signal-gradient"></div>

        <div className="relative max-w-6xl mx-auto px-6">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-5xl md:text-7xl font-bold font-display tracking-tight mb-6 text-center"
          >
            Identify <span className="text-[#FF6A00]">trust gaps</span><br />before they cost you revenue.
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="text-lg md:text-xl text-[#A0A0A0] max-w-3xl mx-auto mb-12 leading-relaxed text-center"
          >
            Subsurface surfaces hidden friction across product, payments, and user experience before it impacts growth.
          </motion.p>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Link
              to="/scan"
              className="group px-8 py-4 bg-[#FF6A00] text-white font-semibold rounded-md hover:bg-[#FF6A00]/90 transition-all duration-300 shadow-[0_0_20px_rgba(255,106,0,0.3)] hover:shadow-[0_0_30px_rgba(255,106,0,0.5)] flex items-center gap-2"
            >
              Run a Discovery Scan
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/pricing"
              className="px-8 py-4 bg-transparent border border-[#FF6A00]/30 text-white font-semibold rounded-md hover:bg-[#FF6A00]/10 hover:border-[#FF6A00] transition-all duration-300"
            >
              Request Structured Review
            </Link>
          </motion.div>
        </div>
      </motion.section>

      {/* What We Do */}
      <section className="py-24 px-6 bg-[#0B0B0D] border-t border-[#FF6A00]/10" data-testid="what-we-do-section">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-4">
              <span className="font-mono text-xs uppercase tracking-widest text-[#FF6A00]">What we do</span>
              <h2 className="text-3xl md:text-4xl font-bold font-display mt-3 tracking-tight leading-tight">
                We find where products <span className="text-[#FF6A00]">quietly</span> lose trust.
              </h2>
            </div>
            <div className="md:col-span-8 space-y-5 text-[#C9C9CC] text-lg leading-relaxed">
              <p>
                Subsurface is a trust and risk architecture consultancy for founders, product teams and operators building digital products where credibility is the product.
              </p>
              <p>
                We don't run generic audits. We investigate specific flows, such as onboarding, checkout, identity, payments and state handling, and show you exactly where users stop believing, stop paying, or stop returning.
              </p>
              <p className="text-[#A0A0A0]">
                Every engagement begins with evidence. Every recommendation is traceable to a real failure pattern, not a framework opinion.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What's Quietly Costing You - Problem Identification */}
      <section className="py-20 px-6 bg-[#1A1A1D]">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold font-display mb-6">
              What's quietly costing you users, revenue or trust?
            </h2>
            <div className="text-lg text-[#A0A0A0] leading-relaxed space-y-4 text-left max-w-3xl mx-auto">
              <p>Most issues don't break your product. They show up as:</p>
              <ul className="space-y-3 ml-6">
                <li className="flex items-start gap-3">
                  <span className="text-[#FF6A00] mt-1">•</span>
                  <span>users dropping off without explanation</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#FF6A00] mt-1">•</span>
                  <span>confusion in key flows</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#FF6A00] mt-1">•</span>
                  <span>payment or state inconsistencies</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#FF6A00] mt-1">•</span>
                  <span>trust signals that don't hold up</span>
                </li>
              </ul>
              <p className="pt-4">If something feels "off" but you can't pinpoint it, this is where Subsurface works.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What's Quietly Costing You - Trust Layer */}
      <section className="py-20 px-6 bg-[#0B0B0D]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-display mb-4">What we surface</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="glass-card p-6 rounded-lg"
            >
              <h3 className="text-xl font-display font-semibold mb-3 text-[#FF6A00]">Hidden conversion friction</h3>
              <p className="text-[#A0A0A0] leading-relaxed">Silent failures in checkout flows, authentication loops, and payment processing that users abandon without reporting.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="glass-card p-6 rounded-lg"
            >
              <h3 className="text-xl font-display font-semibold mb-3 text-[#FF6A00]">Misaligned trust signals</h3>
              <p className="text-[#A0A0A0] leading-relaxed">Inconsistent messaging, unclear error states, and validation gaps that erode user confidence.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="glass-card p-6 rounded-lg"
            >
              <h3 className="text-xl font-display font-semibold mb-3 text-[#FF6A00]">Risk patterns across user journeys</h3>
              <p className="text-[#A0A0A0] leading-relaxed">Recurring issues in multi-step flows where trust breaks down at predictable points.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="glass-card p-6 rounded-lg"
            >
              <h3 className="text-xl font-display font-semibold mb-3 text-[#FF6A00]">Gaps between claims and experience</h3>
              <p className="text-[#A0A0A0] leading-relaxed">Where product promises don't match actual behavior, creating credibility loss.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Free Diagnostic */}
      <section className="py-24 px-6 bg-[#0B0B0D]" data-testid="free-diagnostic-section">
        <div className="max-w-5xl mx-auto">
          <div className="glass-card rounded-2xl p-10 md:p-14 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-72 h-72 bg-[#FF6A00]/10 blur-3xl rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="relative grid md:grid-cols-12 gap-10 items-center">
              <div className="md:col-span-7">
                <span className="font-mono text-xs uppercase tracking-widest text-[#FF6A00]">Free diagnostic</span>
                <h2 className="text-3xl md:text-4xl font-bold font-display mt-3 mb-4 tracking-tight">
                  Start with the Trust Signal Scan
                </h2>
                <p className="text-[#C9C9CC] text-lg leading-relaxed mb-4">
                  A structured 12 question diagnostic across four failure domains, covering Evidence Integrity, Identity Credibility, Operational Visibility and Control Architecture.
                </p>
                <p className="text-[#A0A0A0] leading-relaxed mb-8">
                  You receive an AI-generated trust report with a domain-level score, the highest-risk signals in your product, and a shortlist of the flows most likely to be quietly costing you revenue. No call required.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/scan"
                    data-testid="free-diagnostic-run-scan-btn"
                    className="group inline-flex items-center gap-2 px-6 py-3 bg-[#FF6A00] text-white font-semibold rounded-md hover:bg-[#FF6A00]/90 transition-all shadow-[0_0_20px_rgba(255,106,0,0.25)]"
                  >
                    Run the Scan
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <a
                    href="https://customer-assets.emergentagent.com/job_trust-risk-scanner/artifacts/qf7axdg2_Subsurface_Trust_Signal_Scan_v3.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="free-diagnostic-pdf-btn"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-transparent border border-[#FF6A00]/40 text-white font-semibold rounded-md hover:bg-[#FF6A00]/10 hover:border-[#FF6A00] transition-all"
                  >
                    <Download className="w-4 h-4" />
                    Sample report (PDF)
                  </a>
                </div>
                <div className="mt-6 pt-6 border-t border-[#FF6A00]/10" data-testid="professional-edition-cta">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <div className="font-display font-semibold text-white">Trust Signal Scan. Professional Diagnostic Edition.</div>
                      <div className="text-sm text-[#A0A0A0]">The full self-audit workbook used inside client engagements. £27 on Gumroad.</div>
                    </div>
                    <a
                      href="https://subsurfaceconsulting.gumroad.com/l/huxabf"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="professional-edition-btn"
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-transparent border border-[#FF6A00] text-[#FF6A00] font-semibold rounded-md hover:bg-[#FF6A00] hover:text-white transition-all whitespace-nowrap"
                    >
                      Get it for £27
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </div>
              <div className="md:col-span-5">
                <div className="bg-[#0B0B0D]/60 border border-[#FF6A00]/20 rounded-xl p-6 font-mono text-sm">
                  <div className="flex items-center gap-2 mb-4 text-[#A0A0A0]">
                    <Radar className="w-4 h-4 text-[#FF6A00]" />
                    <span>trust_signal_scan.v3</span>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between"><span className="text-[#A0A0A0]">Evidence Integrity</span><span className="text-[#FF6A00]">68 / 100</span></div>
                    <div className="flex justify-between"><span className="text-[#A0A0A0]">Identity Credibility</span><span className="text-[#FF6A00]">54 / 100</span></div>
                    <div className="flex justify-between"><span className="text-[#A0A0A0]">Operational Visibility</span><span className="text-[#FF6A00]">41 / 100</span></div>
                    <div className="flex justify-between"><span className="text-[#A0A0A0]">Control Architecture</span><span className="text-[#FF6A00]">62 / 100</span></div>
                    <div className="pt-3 mt-3 border-t border-[#FF6A00]/10 flex justify-between text-white">
                      <span>Overall trust signal</span><span className="text-[#FF6A00]">56 / 100</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Entry Paths */}
      <section className="py-16 px-6 bg-[#1A1A1D]">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/scan" className="glass-card p-6 rounded-lg hover:border-[#FF6A00]/50 transition-all group">
              <Radar className="w-8 h-8 text-[#FF6A00] mb-4" />
              <h3 className="text-xl font-display font-semibold mb-2 group-hover:text-[#FF6A00] transition-colors">Trust Signal Scan</h3>
              <p className="text-sm text-[#A0A0A0]">Automated discovery across 4 trust domains</p>
            </Link>

            <Link to="/gateway" className="glass-card p-6 rounded-lg hover:border-[#FF6A00]/50 transition-all group">
              <MessageSquare className="w-8 h-8 text-[#FF6A00] mb-4" />
              <h3 className="text-xl font-display font-semibold mb-2 group-hover:text-[#FF6A00] transition-colors">Subsurface Gateway</h3>
              <p className="text-sm text-[#A0A0A0]">Talk to our AI assistant via Telegram</p>
            </Link>

            <Link to="/submit" className="glass-card p-6 rounded-lg hover:border-[#FF6A00]/50 transition-all group">
              <FileText className="w-8 h-8 text-[#FF6A00] mb-4" />
              <h3 className="text-xl font-display font-semibold mb-2 group-hover:text-[#FF6A00] transition-colors">Manual Submission</h3>
              <p className="text-sm text-[#A0A0A0]">Share your details directly</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Domains Section */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="font-mono text-xs uppercase tracking-widest text-[#A0A0A0]">DIAGNOSTIC FRAMEWORK</span>
            <h2 className="text-4xl md:text-5xl font-bold font-display mt-4 tracking-tight">Four Failure Domains We Analyse</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {domains.map((domain, index) => {
              const Icon = domain.icon;
              return (
                <motion.div
                  key={domain.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  className="glass-card p-8 rounded-lg hover:border-[#FF6A00]/50 transition-all duration-300"
                >
                  <div className="w-12 h-12 bg-[#FF6A00]/10 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-[#FF6A00]" />
                  </div>
                  <h3 className="text-2xl font-display font-semibold mb-3">{domain.title}</h3>
                  <p className="text-[#A0A0A0] leading-relaxed">{domain.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* QA Radar. Our Methodology */}
      <section className="py-24 px-6 bg-[#0B0B0D] border-t border-[#FF6A00]/10" data-testid="qa-radar-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-12 gap-12 items-start">
            <div className="md:col-span-5">
              <span className="font-mono text-xs uppercase tracking-widest text-[#FF6A00]">Our methodology</span>
              <h2 className="text-3xl md:text-5xl font-bold font-display mt-3 mb-6 tracking-tight leading-tight">
                QA Radar. The judgment layer.
              </h2>
              <p className="text-[#C9C9CC] text-lg leading-relaxed mb-5">
                Automation catches failures. Judgment catches the ones that matter.
              </p>
              <p className="text-[#A0A0A0] leading-relaxed mb-8">
                QA Radar is our internal method for turning raw signals into prioritised, explainable risk. We weight every finding by commercial impact, reversibility and user trust damage, so you get a short list of what to fix, not a dump of what's broken.
              </p>
              <a
                href="https://customer-assets.emergentagent.com/job_trust-risk-scanner/artifacts/ctsyhyhm_QA_Radar_Methodology_FIXED.pdf"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="qa-radar-pdf-btn"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF6A00] text-white font-semibold rounded-md hover:bg-[#FF6A00]/90 transition-all"
              >
                <Download className="w-4 h-4" />
                Read the methodology (PDF)
              </a>
            </div>
            <div className="md:col-span-7">
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { n: '01', t: 'Evidence capture', d: 'Every finding is tied to a reproducible trace, screenshot or log line, never opinion.' },
                  { n: '02', t: 'Severity weighting', d: 'Scored on user trust damage, commercial impact and how reversible the failure is.' },
                  { n: '03', t: 'Pattern clustering', d: 'Isolated bugs are grouped into trust-failure patterns so you fix the cause, not the symptom.' },
                  { n: '04', t: 'Judgment call', d: 'A ranked shortlist of what to fix this week, this month, and what to safely deprioritise.' }
                ].map((s) => (
                  <div key={s.n} className="glass-card rounded-lg p-5">
                    <div className="font-mono text-xs text-[#FF6A00]/70 mb-2">{s.n}</div>
                    <div className="font-display font-semibold text-lg mb-2">{s.t}</div>
                    <div className="text-sm text-[#A0A0A0] leading-relaxed">{s.d}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Flow */}
      <section className="py-24 px-6 bg-[#1A1A1D]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className="font-mono text-xs uppercase tracking-widest text-[#A0A0A0]">SERVICE FLOW</span>
            <h2 className="text-4xl md:text-5xl font-bold font-display mt-4 tracking-tight">What happens next?</h2>
          </div>

          <div className="space-y-8">
            {serviceFlow.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="flex gap-6 items-start"
              >
                <div className="text-5xl font-mono font-bold text-[#FF6A00]/20">{step.number}</div>
                <div className="flex-1">
                  <h3 className="text-2xl font-display font-semibold mb-2">{step.title}</h3>
                  <p className="text-[#A0A0A0] leading-relaxed">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Work With Us */}
      <section className="py-24 px-6 bg-[#0B0B0D]" data-testid="work-with-us-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <span className="font-mono text-xs uppercase tracking-widest text-[#FF6A00]">Work with us</span>
            <h2 className="text-3xl md:text-5xl font-bold font-display mt-3 tracking-tight">Four ways to engage.</h2>
            <p className="text-[#A0A0A0] mt-4">All async. No calls. Everything documented.</p>
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
            <div className="glass-card rounded-xl p-8 flex flex-col" data-testid="engage-scan-card">
              <Compass className="w-8 h-8 text-[#FF6A00] mb-5" />
              <div className="font-mono text-xs text-[#A0A0A0] mb-2">FREE</div>
              <h3 className="text-2xl font-display font-semibold mb-3">Discovery Scan</h3>
              <p className="text-[#A0A0A0] leading-relaxed mb-6 flex-1">
                Self-serve diagnostic. Get a trust score and top risk signals in under 10 minutes. Ideal for an honest first read.
              </p>
              <Link to="/scan" className="inline-flex items-center gap-2 text-[#FF6A00] font-semibold hover:gap-3 transition-all">
                Start the scan <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="glass-card rounded-xl p-8 flex flex-col" data-testid="engage-review-card">
              <Handshake className="w-8 h-8 text-[#FF6A00] mb-5" />
              <div className="font-mono text-xs text-[#A0A0A0] mb-2">£295</div>
              <h3 className="text-2xl font-display font-semibold mb-3">Structured Review</h3>
              <p className="text-[#A0A0A0] leading-relaxed mb-6 flex-1">
                Expert investigation of one critical flow, such as onboarding, checkout, payments or identity. Written QA Radar report in 5 working days.
              </p>
              <Link to="/contact" className="inline-flex items-center gap-2 text-[#FF6A00] font-semibold hover:gap-3 transition-all">
                Start a review <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="glass-card rounded-xl p-8 flex flex-col border-[#FF6A00]/40 relative" data-testid="engage-audit-card">
              <div className="absolute -top-3 left-8 px-3 py-1 bg-[#FF6A00] text-white text-xs font-semibold rounded-full">Most chosen</div>
              <ShieldCheck className="w-8 h-8 text-[#FF6A00] mb-5" />
              <div className="font-mono text-xs text-[#A0A0A0] mb-2">£750</div>
              <h3 className="text-2xl font-display font-semibold mb-3">Pre-Release QA Audit</h3>
              <p className="text-[#A0A0A0] leading-relaxed mb-6 flex-1">
                Full trust and risk audit before you ship. All critical flows, edge cases and failure patterns. 14 days of Telegram follow-up.
              </p>
              <Link to="/contact" className="inline-flex items-center gap-2 text-[#FF6A00] font-semibold hover:gap-3 transition-all">
                Book an audit <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="glass-card rounded-xl p-8 flex flex-col" data-testid="engage-retainer-card">
              <Activity className="w-8 h-8 text-[#FF6A00] mb-5" />
              <div className="font-mono text-xs text-[#A0A0A0] mb-2">£500 / MONTH</div>
              <h3 className="text-2xl font-display font-semibold mb-3">Advisory Retainer</h3>
              <p className="text-[#A0A0A0] leading-relaxed mb-6 flex-1">
                Ongoing trust architecture oversight. Delta reports on every major release, monthly judgment review, direct Telegram access. 3 month minimum.
              </p>
              <Link to="/contact" className="inline-flex items-center gap-2 text-[#FF6A00] font-semibold hover:gap-3 transition-all">
                Talk about a retainer <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Signal, Not Siren. Book */}
      <section className="py-24 px-6 bg-[#1A1A1D]" data-testid="signal-not-siren-section">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-5">
              <div className="relative aspect-[3/4] rounded-lg overflow-hidden border border-[#FF6A00]/30 shadow-[0_20px_60px_rgba(255,106,0,0.15)]">
                <img
                  src="https://customer-assets.emergentagent.com/job_trust-risk-scanner/artifacts/yschmmzp_0C3F733E-EAC5-4B37-981C-2A8C6A08043C.png"
                  alt="Signal, Not Siren book cover by Garry Cameron — Subsurface"
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover"
                  data-testid="book-cover-img"
                />
                <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-5 py-3 bg-gradient-to-t from-black/90 to-transparent">
                  <BookOpen className="w-4 h-4 text-[#FF6A00]" />
                  <span className="font-mono text-[10px] tracking-widest text-[#FF6A00]">OUT NOW</span>
                </div>
              </div>
            </div>
            <div className="md:col-span-7">
              <span className="font-mono text-xs uppercase tracking-widest text-[#FF6A00]">The book</span>
              <h2 className="text-3xl md:text-5xl font-bold font-display mt-3 mb-6 tracking-tight leading-tight">
                Signal, Not Siren.
              </h2>
              <p className="text-[#C9C9CC] text-lg leading-relaxed mb-5">
                A working field guide on why modern products quietly lose user trust, and how to surface it before it costs revenue.
              </p>
              <p className="text-[#A0A0A0] leading-relaxed mb-8">
                Written from inside real engagements. No frameworks for their own sake. Just the failure patterns we see repeatedly, the ones that never appear in a bug tracker, and the structural questions that predict them.
              </p>
              <a
                href="https://www.amazon.co.uk/dp/B0GX3BBJ21"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="book-buy-amazon-btn"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF6A00] text-white font-semibold rounded-md hover:bg-[#FF6A00]/90 transition-all shadow-[0_0_20px_rgba(255,106,0,0.25)]"
              >
                <BookOpen className="w-4 h-4" />
                Buy on Amazon
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* LinkedIn Authority Layer */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto text-center glass-card p-8 rounded-lg">
          <Linkedin className="w-12 h-12 text-[#FF6A00] mx-auto mb-4" />
          <h3 className="text-2xl font-display font-semibold mb-3">Real-world trust breakdowns</h3>
          <p className="text-[#A0A0A0] mb-6">Follow Subsurface on LinkedIn for case studies, trust signal analysis, and revenue integrity insights.</p>
          <a
            href="https://www.linkedin.com/company/subsurface-qa-risk/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF6A00] text-white font-semibold rounded-md hover:bg-[#FF6A00]/90 transition-colors"
          >
            <Linkedin className="w-4 h-4" />
            View on LinkedIn
          </a>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold font-display mb-6 tracking-tight">Ready to surface hidden risks?</h2>
          <p className="text-lg text-[#A0A0A0] mb-8 leading-relaxed">Start with a discovery scan or speak directly to Subsurface.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/scan"
              className="px-8 py-4 bg-[#FF6A00] text-white font-semibold rounded-md hover:bg-[#FF6A00]/90 transition-all"
            >
              Run Discovery Scan
            </Link>
            <Link
              to="/pricing"
              className="px-8 py-4 bg-transparent border border-[#FF6A00]/30 text-white font-semibold rounded-md hover:bg-[#FF6A00]/10 transition-all"
            >
              Request Structured Review
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      {showLeadModal && (
        <LeadCaptureModal
          isOpen={showLeadModal}
          onClose={() => setShowLeadModal(false)}
          modalType={modalType}
        />
      )}
    </div>
  );
};

export default LandingPage;
